import * as React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  OutlinedInput,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Camera as CameraIcon, Trash as TrashIcon, User as UserIcon } from '@phosphor-icons/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'sonner';

import { useUser } from '@/hooks/v2/use-user';
import axios from '@/lib/v2/axios';
import { getS3PresignedUrlFromPath } from '@/utils/s3';

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  phoneNumber: Yup.string().nullable(),
});

export function UserDetails(): React.JSX.Element {
  const { user, checkSession } = useUser();
  const [isEditing, setIsEditing] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = React.useState(false);
  const [avatarPreview, setAvatarPreview] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!user?.avatar?.path) {
      setAvatarUrl(null);
      return;
    }
    getS3PresignedUrlFromPath(user.avatar.path)
      .then(setAvatarUrl)
      .catch(() => setAvatarUrl(user.avatar!.path));
  }, [user?.avatar?.path]);

  const displayAvatarUrl = avatarPreview ?? avatarUrl ?? undefined;
  const hasAvatar = Boolean(user?.avatar?.id ?? user?.avatar?.path);

  const handleAvatarSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    // Show instant preview
    const reader = new FileReader();
    reader.onload = (e) => setAvatarPreview(e.target?.result as string);
    reader.readAsDataURL(file);

    // Upload immediately
    try {
      setIsUploadingAvatar(true);
      const formData = new FormData();
      formData.append('avatar', file);
      const response = await axios.post('/api/v2/auth/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        toast.success('Avatar updated');
        await checkSession();
        setAvatarPreview(null);
      } else {
        toast.error(response.data.message || 'Failed to upload avatar');
        setAvatarPreview(null);
      }
    } catch {
      toast.error('Failed to upload avatar. Please try again.');
      setAvatarPreview(null);
    } finally {
      setIsUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAvatarRemove = async () => {
    try {
      setIsUploadingAvatar(true);
      const response = await axios.delete('/api/v2/auth/avatar');
      if (response.data.success) {
        toast.success('Avatar removed');
        setAvatarPreview(null);
        await checkSession();
      } else {
        toast.error(response.data.message || 'Failed to remove avatar');
      }
    } catch {
      toast.error('Failed to remove avatar. Please try again.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      firstName: user?.first_name ?? '',
      lastName: user?.last_name ?? '',
      phoneNumber: user?.phone ?? '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setIsSubmitting(true);
        const response = await axios.put('/api/v2/auth/user', {
          first_name: values.firstName,
          last_name: values.lastName,
          phone: values.phoneNumber || null,
        });
        if (response.status === 200) {
          toast.success('Profile updated successfully');
          setIsEditing(false);
          await checkSession();
        }
      } catch (error: any) {
        if (error.response) {
          const { status, data } = error.response;
          if (status === 422) {
            toast.error('Please check your input and try again');
          } else {
            toast.error(data?.message || 'Failed to update profile');
          }
        } else {
          toast.error('Network error. Please check your connection');
        }
      } finally {
        setIsSubmitting(false);
        setSubmitting(false);
      }
    },
  });

  const handleCancel = () => {
    setIsEditing(false);
    formik.resetForm();
  };

  if (!user) {
    return (
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Skeleton height={60} variant="rectangular" />
            <Skeleton height={40} variant="rectangular" />
            <Skeleton height={40} variant="rectangular" />
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        avatar={
          <Avatar>
            <UserIcon fontSize="var(--Icon-fontSize)" />
          </Avatar>
        }
        title="Basic details"
      />
      <CardContent>
        <Stack spacing={3}>
          {/* Avatar — always interactive, independent of edit mode */}
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                border: '1px dashed var(--mui-palette-divider)',
                borderRadius: '50%',
                display: 'inline-flex',
                p: '4px',
                position: 'relative',
              }}
            >
              <Box sx={{ borderRadius: 'inherit', position: 'relative' }}>
                <Avatar
                  src={displayAvatarUrl}
                  sx={{ '--Avatar-size': '100px', cursor: isUploadingAvatar ? 'default' : 'pointer' }}
                  onClick={() => !isUploadingAvatar && fileInputRef.current?.click()}
                >
                  {!displayAvatarUrl && <UserIcon fontSize="var(--icon-fontSize-lg)" />}
                </Avatar>

                {/* Hover overlay: change photo or remove */}
                {!isUploadingAvatar && (
                  <Box
                    sx={{
                      alignItems: 'center',
                      bgcolor: 'rgba(0,0,0,0.5)',
                      borderRadius: 'inherit',
                      bottom: 0,
                      color: 'common.white',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      justifyContent: 'center',
                      left: 0,
                      opacity: 0,
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      zIndex: 1,
                      transition: 'opacity 0.2s',
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <Box
                      sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <CameraIcon size={16} />
                      <Typography color="inherit" variant="caption" fontWeight={600}>
                        {hasAvatar ? 'Change' : 'Upload'}
                      </Typography>
                    </Box>
                    {hasAvatar && (
                      <Box
                        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAvatarRemove();
                        }}
                      >
                        <TrashIcon size={14} />
                        <Typography color="inherit" variant="caption" fontWeight={600}>
                          Remove
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Uploading spinner overlay */}
                {isUploadingAvatar && (
                  <Box
                    sx={{
                      alignItems: 'center',
                      bgcolor: 'rgba(0,0,0,0.45)',
                      borderRadius: 'inherit',
                      bottom: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      left: 0,
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={28} sx={{ color: 'common.white' }} />
                  </Box>
                )}
              </Box>
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarSelect}
              style={{ display: 'none' }}
            />

            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Click the photo to upload a new avatar.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                JPG, PNG or GIF · max 5 MB
              </Typography>
              {hasAvatar && (
                <Button
                  color="error"
                  size="small"
                  variant="text"
                  disabled={isUploadingAvatar}
                  onClick={handleAvatarRemove}
                  sx={{ alignSelf: 'flex-start', px: 0 }}
                >
                  Remove photo
                </Button>
              )}
            </Stack>
          </Stack>

          {/* Profile fields */}
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <FormControl sx={{ flex: '1 1 auto' }}>
                  <InputLabel>First name</InputLabel>
                  <OutlinedInput
                    disabled={!isEditing}
                    error={formik.touched.firstName ? Boolean(formik.errors.firstName) : undefined}
                    name="firstName"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.firstName}
                  />
                  {formik.touched.firstName && formik.errors.firstName ? (
                    <FormHelperText error>{formik.errors.firstName}</FormHelperText>
                  ) : null}
                </FormControl>
                <FormControl sx={{ flex: '1 1 auto' }}>
                  <InputLabel>Last name</InputLabel>
                  <OutlinedInput
                    disabled={!isEditing}
                    error={formik.touched.lastName ? Boolean(formik.errors.lastName) : undefined}
                    name="lastName"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.lastName}
                  />
                  {formik.touched.lastName && formik.errors.lastName ? (
                    <FormHelperText error>{formik.errors.lastName}</FormHelperText>
                  ) : null}
                </FormControl>
              </Stack>

              <FormControl disabled>
                <InputLabel>Email address</InputLabel>
                <OutlinedInput name="email" type="email" value={user?.email} />
                <FormHelperText>
                  Please <Link variant="inherit">contact us</Link> to change your email
                </FormHelperText>
              </FormControl>

              <FormControl>
                <InputLabel>Phone number</InputLabel>
                <OutlinedInput
                  disabled={!isEditing}
                  error={formik.touched.phoneNumber ? Boolean(formik.errors.phoneNumber) : undefined}
                  name="phoneNumber"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  placeholder="Enter your phone number (optional)"
                  value={formik.values.phoneNumber}
                />
                {formik.touched.phoneNumber && formik.errors.phoneNumber ? (
                  <FormHelperText error>{formik.errors.phoneNumber}</FormHelperText>
                ) : (
                  <FormHelperText>Phone number is optional</FormHelperText>
                )}
              </FormControl>
            </Stack>
          </form>
        </Stack>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        {!isEditing ? (
          <Button color="secondary" onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        ) : (
          <>
            <Button color="secondary" disabled={isSubmitting} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              disabled={isSubmitting || !formik.isValid}
              onClick={() => formik.handleSubmit()}
              variant="contained"
            >
              {isSubmitting ? 'Saving...' : 'Save changes'}
            </Button>
          </>
        )}
      </CardActions>
    </Card>
  );
}
